<template>
  <div
    class="plato-slot"
    :class="{
      'is-movible': movible,
      'is-origen': origen,
      'is-destino': destino,
      'is-posible': posible && !destino,
    }"
    :data-plato-fecha="fecha"
    :data-plato-slot="tipo"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <button
      v-if="movible"
      type="button"
      class="plato-grip"
      :disabled="bloqueado"
      :aria-pressed="origen ? 'true' : 'false'"
      :aria-label="ariaLabel"
      draggable="false"
      @keydown.enter.prevent="$emit('activar')"
      @keydown.space.prevent="$emit('activar')"
    >
      <i class="bi bi-grip-vertical" aria-hidden="true"></i>
    </button>
    <div class="plato-slot-cuerpo">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'NutricionPlatoSlot',
  props: {
    fecha: { type: String, required: true },
    tipo: {
      type: String,
      required: true,
      validator: (v) => v === 'comida' || v === 'cena',
    },
    movible: { type: Boolean, default: false },
    soloAsa: { type: Boolean, default: false },
    bloqueado: { type: Boolean, default: false },
    origen: { type: Boolean, default: false },
    destino: { type: Boolean, default: false },
    posible: { type: Boolean, default: false },
    ariaLabel: { type: String, default: 'Arrastrar plato' },
  },
  emits: ['iniciar', 'mover', 'soltar', 'cancelar-puntero', 'activar'],
  setup(props, { emit }) {
    const esTactil = (event) => event.pointerType === 'touch' || event.pointerType === 'pen';
    const esAsa = (event) => !!event.target?.closest?.('.plato-grip');

    const onPointerDown = (event) => {
      if (!props.movible || props.bloqueado) return;
      if (event.button != null && event.button !== 0) return;
      if (event.target?.closest?.('input, textarea, select, option')) return;
      const tactil = esTactil(event);
      if ((tactil || props.soloAsa) && !esAsa(event)) return;
      if (tactil) event.preventDefault();
      if (tactil) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (err) {
          /* El puntero puede haberse soltado antes de capturarlo. */
        }
      }
      emit('iniciar', event);
    };

    const onPointerMove = (event) => {
      if (!esTactil(event)) return;
      if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
      emit('mover', event);
    };

    const onPointerUp = (event) => {
      if (!props.movible || !esTactil(event)) return;
      emit('soltar', event);
    };

    const onPointerCancel = (event) => {
      if (!esTactil(event)) return;
      emit('cancelar-puntero', event);
    };

    return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
  },
};
</script>

<style scoped>
.plato-slot {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  min-width: 0;
  border-radius: 0.65rem;
  padding: 0.15rem 0.35rem 0.15rem 0.1rem;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.plato-slot-cuerpo {
  min-width: 0;
  flex: 1;
}

.plato-grip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid rgba(13, 110, 253, 0.28);
  border-radius: 0.5rem;
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
  touch-action: none;
  cursor: grab;
  flex: 0 0 auto;
  font-size: 1.15rem;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

.plato-grip:hover,
.plato-grip:focus-visible {
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
  outline: none;
}

.plato-grip:disabled {
  opacity: 0.45;
  cursor: progress;
}

.plato-slot.is-movible {
  border-color: rgba(13, 110, 253, 0.14);
  background: rgba(13, 110, 253, 0.035);
}

.plato-slot.is-origen {
  border-color: rgba(13, 110, 253, 0.55);
  background: rgba(13, 110, 253, 0.08);
}

.plato-slot.is-posible {
  border-style: dashed;
  border-color: rgba(13, 110, 253, 0.5);
  background: rgba(13, 110, 253, 0.04);
}

.plato-slot.is-destino {
  border-style: solid;
  border-color: #198754;
  background: rgba(25, 135, 84, 0.14);
  box-shadow: 0 0 0 2px rgba(25, 135, 84, 0.22);
}

.plato-slot.is-destino .plato-slot-cuerpo::before {
  content: 'Soltar para intercambiar';
  display: block;
  margin-bottom: 0.12rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #146c43;
}

@media (hover: hover) and (pointer: fine) {
  .plato-slot.is-movible {
    cursor: grab;
  }

  .plato-slot.is-movible :deep(a) {
    cursor: pointer;
  }

  .plato-slot.is-movible:hover {
    background: rgba(13, 110, 253, 0.05);
  }
}

@media (max-width: 767.98px) {
  .plato-grip {
    width: 2.75rem;
    height: 2.75rem;
    font-size: 1.35rem;
  }

  .plato-slot.is-movible {
    padding: 0.35rem 0.55rem 0.35rem 0.2rem;
    border-color: rgba(13, 110, 253, 0.18);
    background: #fff;
  }
}
</style>
